import React, { useCallback, useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import { Badge, DefaultPrimaryButton } from "../../components/micro/input";
import ReactSelect from "react-select";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import {
  getSalesList,
  saleDelete,
  updateSaleStatus,
} from "../../redux/sales/slice";
import moment from "moment";
import { companiesList } from "../../redux/companies/slice";
import ReactDatePicker from "react-datepicker";
import ViewSales from "./ViewSales";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useLocation } from "react-router-dom";

const SalesList = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isModal, setIsModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [technicianIdToDelete, setTechnicianIdToDelete] = useState([]);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [type, setType] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const location = useLocation();
  const selectedTab = location.state?.tab || "all";
  const [activeTab, setActiveTab] = useState(selectedTab);

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getSalesListData, loading, companiesListData } = useSelector(
    (store) => ({
      getSalesListData: store?.salesData?.getSalesListStatus?.data?.data,
      companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
      loading: store?.salesData?.loading,
    })
  );

  const refreshSalesList = useCallback(() => {
    dispatch(
      getSalesList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        company_name: selectedCompany?.label || "",
        types: activeTab || "",
        date_range: DateRange || "",
      })
    );
  }, [
    dispatch,
    searchValue,
    pageCount,
    pageLimit,
    sortBy,
    sortDirection,
    selectedCompany,
    activeTab,
    DateRange,
  ]);

  useEffect(() => {
    setSelectedRows([]);
    refreshSalesList();
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
  }, [
    searchValue,
    pageCount,
    pageLimit,
    activeTab,
    sortBy,
    sortDirection,
    selectedCompany,
    activeTab,
    DateRange,
  ]);

  const updateStatus = (saleId, actionType) => {
    setIsLoading(true);

    let statusUpdate = "";

    switch (actionType) {
      case "autoCount":
        statusUpdate = "autocount";
        break;
      case "unpaid":
        statusUpdate = "unpaid";
        break;
      case "paid":
        statusUpdate = "paid";
        break;
      default:
        setIsLoading(false);
        return;
    }

    dispatch(
      updateSaleStatus({
        data: {
          ids: saleId || selectedRows?.id,
          status: statusUpdate,
        },
      })
    ).then((res) => {
      if (res.type === "updateSaleStatus/fulfilled") {
        dispatch(
          getSalesList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            company_name: selectedCompany?.label || "",
            types: activeTab || "",
            date_range: DateRange || "",
          })
        );

        setSelectedRows([]);
        setIsSelectAll(false);

        setIsLoading(false);
        setShowConfirmationModal(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const handelSaleDelete = () => {
    setIsLoading(true);

    dispatch(saleDelete({ ids: technicianIdToDelete })).then((res) => {
      if (res.type === "saleDelete/fulfilled") {
        dispatch(
          getSalesList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            company_name: selectedCompany?.label || "",
            types: activeTab || "",
            date_range: DateRange || "",
          })
        );
        setIsLoading(false);
        setConfirmDelete(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const companyOptions = (companiesListData?.data || companiesListData)?.map(
    (companies) => ({
      value: companies.id,
      label: companies.name,
    })
  );

  const handleCompanyChange = (selectedOption) => {
    setSelectedCompany(selectedOption);
  };
  const handleAction = async (selectedOption) => {
    if (!selectedOption?.action) return;

    setSelectedAction(selectedOption);

    try {
      await selectedOption.action();
    } catch (error) {
      console.error("Error executing action:", error);
    } finally {
      setSelectedAction(null);
    }
  };

  const tabs = [
    { label: "All", value: "all" },
    { label: "Quotation", value: "Quotation" },
    { label: "Invoice", value: "Invoice" },
    { label: "Receipt", value: "Receipt" },
  ];

  const handleRowSelection = (id, isChecked) => {
    setSelectedRows((prevSelected) => {
      const updatedSelection = new Set(prevSelected);

      if (isChecked) {
        updatedSelection.add(id);
      } else {
        updatedSelection.delete(id);
      }

      if (updatedSelection.size === getSalesListData?.data?.length) {
        setIsSelectAll(true);
      } else {
        setIsSelectAll(false);
      }

      return Array.from(updatedSelection);
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setIsSelectAll(checked);

    if (checked) {
      setSelectedRows(getSalesListData?.data?.map((item) => item));
    } else {
      setSelectedRows([]);
    }
  };

  const getActionOptions = (element) => {
    const commonActions = (
      <>
        <li
          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
          onClick={() => {
            setSelectedSaleId([element.id]);
            setIsModal(true);
          }}
        >
          View
        </li>
        <li
          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
          onClick={() => {
            setSelectedSaleId([element.id]);
            setIsModal(true);
          }}
        >
          Download
        </li>
      </>
    );

    if (element?.type === "quotation") {
      if (element?.status === "in system") {
        return (
          <>
            {commonActions}
            {/* <li
              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
              onClick={() => {
                setSelectedSaleId([element.id]);
                setShowConfirmationModal(true);
                setActionType("autoCount");
              }}
            >
              Pushed to AutoCount's Quotation
            </li> */}
            <li
              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
              onClick={() => {
                setSelectedSaleId([element.id]);
                setShowConfirmationModal(true);
                setActionType("unpaid");
              }}
            >
              Convert to Invoice
            </li>
            {activeTab === "Quotation" && (
              <li
                className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                onClick={() => {
                  setConfirmDelete(true);
                  setTechnicianIdToDelete([element?.id]);
                }}
              >
                Delete
              </li>
            )}
          </>
        );
      }
      if (element?.status === "autocount") {
        return (
          <>
            {commonActions}
            <li
              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
              onClick={() => {
                setSelectedSaleId([element.id]);
                setShowConfirmationModal(true);
                setActionType("unpaid");
              }}
            >
              Convert to Invoice
            </li>
            {activeTab === "Quotation" && (
              <li
                className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                onClick={() => {
                  setSelectedSaleId([element.id]);
                  setConfirmEmail(true);
                  setType("sendQuotation");
                }}
              >
                Send Quotation (Email)
              </li>
            )}
          </>
        );
      }
    }
    if (element?.type === "invoice" && element?.status === "unpaid") {
      return (
        <>
          {commonActions}
          {activeTab === "Invoice" && (
            <>
              <li
                className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                onClick={() => {
                  setSelectedSaleId([element.id]);
                  setConfirmEmail(true);
                  setType("sendInvoice");
                }}
              >
                Send Invoice (Email)
              </li>
              <li
                className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                onClick={() => {
                  setSelectedSaleId([element.id]);
                  setConfirmEmail(true);
                  setType("sendInvoiceWithReport");
                }}
              >
                Send Invoice (Email) With Reports
              </li>
            </>
          )}
          <li
            className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
            onClick={() => {
              setSelectedSaleId([element.id]);
              setShowConfirmationModal(true);
              setActionType("paid");
              setType("convertToPaid");
            }}
          >
            Convert to Paid(Receipt)
          </li>
        </>
      );
    }
    if (element?.type === "receipt" && element?.status === "paid") {
      return (
        <>
          {commonActions}
          {activeTab === "Receipt" && (
            <li
              className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
              onClick={() => {
                setSelectedSaleId([element.id]);
                setConfirmEmail(true);
                setType("sendReceipt");
              }}
            >
              Send Receipt (Email)
            </li>
          )}
        </>
      );
    }
    return null;
  };

  const bulkOptions = () => {
    const options = [];
    const addedActions = new Set();

    selectedRows?.forEach((element) => {
      const downloadAction = {
        value: "download",
        label: "Download",
        action: () => {
          const selectedIds = selectedRows?.map((item) => item.id);
          setSelectedSaleId(selectedIds);
          setIsModal(true);
        },
      };
      if (!addedActions.has(downloadAction.value)) {
        options.push(downloadAction);
        addedActions.add(downloadAction.value);
      }

      if (element?.status === "in system") {
        // const autoCountAction = {
        //   value: "autoCount",
        //   label: "Pushed to AutoCount's Quotation",
        //   action: () => {
        //     const selectedIds = selectedRows?.map((item) => item.id);
        //     setSelectedSaleId(selectedIds);
        //     setShowConfirmationModal(true);
        //     setActionType("autoCount");
        //   },
        // };
        // if (!addedActions.has(autoCountAction.value)) {
        //   options.push(autoCountAction);
        //   addedActions.add(autoCountAction.value);
        // }

        const convertInvoiceAction = {
          value: "convertInvoice",
          label: "Convert to Invoice",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setShowConfirmationModal(true);
            setActionType("unpaid");
          },
        };
        if (!addedActions.has(convertInvoiceAction.value)) {
          options.push(convertInvoiceAction);
          addedActions.add(convertInvoiceAction.value);
        }

        const deleteAction = {
          value: "delete",
          label: "Delete",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setConfirmDelete(true);
            setTechnicianIdToDelete(selectedIds);
          },
        };
        if (!addedActions.has(deleteAction.value)) {
          options.push(deleteAction);
          addedActions.add(deleteAction.value);
        }
      }

      if (element?.status === "autocount") {
        const convertInvoiceAction = {
          value: "convertInvoice",
          label: "Convert to Invoice",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setShowConfirmationModal(true);
            setActionType("unpaid");
          },
        };
        if (!addedActions.has(convertInvoiceAction.value)) {
          options.push(convertInvoiceAction);
          addedActions.add(convertInvoiceAction.value);
        }

        const sendEmailAction = {
          value: "sendEmail",
          label: "Send Quotation (Email)",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setConfirmEmail(true);
            setType("sendQuotation");
          },
        };
        if (!addedActions.has(sendEmailAction.value)) {
          options.push(sendEmailAction);
          addedActions.add(sendEmailAction.value);
        }
      }

      if (element?.status === "unpaid") {
        const sendInvoiceAction = {
          value: "sendInvoice",
          label: "Send Invoice",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setConfirmEmail(true);
            setType("sendInvoice");
          },
        };
        if (!addedActions.has(sendInvoiceAction.value)) {
          options.push(sendInvoiceAction);
          addedActions.add(sendInvoiceAction.value);
        }

        const sendInvoiceWithReportAction = {
          value: "sendInvoiceWithReport",
          label: "Send Invoice With Report",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setConfirmEmail(true);
            setType("sendInvoiceWithReport");
          },
        };
        if (!addedActions.has(sendInvoiceWithReportAction.value)) {
          options.push(sendInvoiceWithReportAction);
          addedActions.add(sendInvoiceWithReportAction.value);
        }

        const convertToPaidAction = {
          value: "convertToPaid",
          label: "Convert to Paid(Receipt)",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setShowConfirmationModal(true);
            setActionType("paid");
          },
        };
        if (!addedActions.has(convertToPaidAction.value)) {
          options.push(convertToPaidAction);
          addedActions.add(convertToPaidAction.value);
        }
      }

      if (element?.status === "paid") {
        const sendReceiptAction = {
          value: "sendReceipt",
          label: "Send Receipt (Email)",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            setSelectedSaleId(selectedIds);
            setConfirmEmail(true);
            setType("sendReceipt");
          },
        };
        if (!addedActions.has(sendReceiptAction.value)) {
          options.push(sendReceiptAction);
          addedActions.add(sendReceiptAction.value);
        }
      }
    });

    return options;
  };

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div className="flex items-center">
          <input
            type="checkbox"
            className="w-4 h-4 mr-2 accent-themeBtn"
            checked={isSelectAll}
            onChange={handleSelectAll}
          />
          <div
            className="flex items-center"
            onClick={() => {
              setSortBy("id");
              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
            }}
          >
            <span>ID.</span>
            <SortDownArrow
              className={`ml-1 transition-transform duration-300 ${
                sortDirection === "asc" && sortBy === "id" ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company_name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company_name"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("type");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          Types
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "type" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Status </span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "status" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("amount");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Amount</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "amount" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  const DataRows = () => (
    <>
      {getSalesListData?.data?.map((element, index) => (
        <tr
          key={index}
          className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-4 py-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="w-4 h-4 mr-2 accent-themeBtn"
                onChange={(e) => handleRowSelection(element, e.target.checked)}
                checked={selectedRows?.includes(element)}
              />
              {element?.unique_id || "-"}
            </div>
          </td>
          <td className="px-5 py-3">
            {element?.company_name
              ? element?.company_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  element?.type === "receipt"
                    ? "border-bgGreen text-bgGreen"
                    : element?.type === "invoice"
                    ? "border-chipBlue text-chipBlue"
                    : element?.type === "quotation"
                    ? "border-gray text-black"
                    : ""
                }`}
                value={
                  element?.type === "receipt"
                    ? "Receipt"
                    : element?.type === "invoice"
                    ? "Invoice"
                    : element?.type === "quotation"
                    ? "Quotation"
                    : "" || "-"
                }
              />
            </div>
          </td>
          <td className="px-5 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  element?.status === "paid"
                    ? "bg-bgGreen text-white"
                    : element?.status === "autocount"
                    ? "bg-chipBlue text-white"
                    : element?.status === "unpaid"
                    ? "bg-orange-600 text-white"
                    : element?.status === "in system"
                    ? "border-buttonGray bg-buttonGray text-black"
                    : ""
                }`}
                value={
                  element?.status === "paid"
                    ? "Paid"
                    : element?.status === "autocount"
                    ? "AutoCount"
                    : element?.status === "unpaid"
                    ? "Unpaid"
                    : element?.status === "in system"
                    ? "In System"
                    : "" || "-"
                }
              />
            </div>
          </td>
          <td className="px-5 py-3 truncate">
            {" "}
            {element?.updated_at
              ? moment(element.updated_at).format("DD/MM/YYYY")
              : "-"}
          </td>
          <td className="px-5 py-3">${Number(element?.amount).toFixed(2)}</td>
          <td className="flex px-5 py-3">
            <Action optionList={getActionOptions(element)} />
          </td>
        </tr>
      ))}
    </>
  );

  const handleSalesExportClick = () => {
    if (!getSalesListData?.data || getSalesListData?.data.length === 0) {
      alert("No data available to export!");
      return;
    }

    const header = [
      "Unique ID",
      "Company Name",
      "Type",
      "Status",
      "Updated At",
      "Amount",
    ];

    const rows = getSalesListData?.data.map((sale) => [
      `"${sale?.unique_id || "-"}"`,
      `"${sale?.company_name || "-"}"`,
      `"${sale?.type || "-"}"`,
      `"${sale?.status || "-"}"`,
      `"${
        sale?.updated_at ? moment(sale.updated_at).format("DD/MM/YYYY") : "-"
      }"`,
      `"${sale?.amount || "-"}"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "sales_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">Sales</h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Sales"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
        </div>
        <div className="flex justify-end mt-8">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              className={`py-2 w-full font-normal mb-5 h-full px-4 uppercase text-sm border-b-2 transition duration-300 font-Proxima ${
                activeTab === tab.value
                  ? "border-themeBtn text-themeBtn"
                  : "border-transparent text-textSecondary hover:text-themeBtn hover:border-theme text-gray-dark"
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.value}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Picker */}
          <div className="w-full sm:w-[220px] md:w-[240px] min-w-[160px]">
            <div className="text-black border border-borderTheme/80 flex justify-between relative rounded w-full px-2 py-[6px] items-center transition duration-300 ease-in-out">
              <ReactDatePicker
                selected={dateRange[0]}
                onChange={(dates) => setDateRange(dates)}
                selectsRange
                isClearable
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date Range"
                className="w-full"
              />
            </div>
          </div>

          {/* Company / Bulk Action Select */}
          <div className="w-full sm:w-[200px] md:w-[220px] min-w-[160px]">
            {activeTab === "all" ? (
              <ReactSelect
                className="w-full text-black p-0 font-Proxima"
                id="company"
                placeholder="Company"
                options={companyOptions}
                isClearable
                getOptionValue={(option) => option?.value}
                getOptionLabel={(option) => option?.label}
                value={selectedCompany}
                onChange={handleCompanyChange}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: `1px solid #0000003B`,
                    borderColor: selectedCompany ? "#F15B29" : "#0000003B",
                    borderRadius: "0.3rem",
                    boxShadow: "none",
                    "&:hover": {
                      border: "1px solid #0000003B",
                    },
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? "#F15B29" : "white",
                    "&:active": {
                      backgroundColor: state.isSelected
                        ? "#F15B29"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  }),
                }}
              />
            ) : (
              <ReactSelect
                className="w-full text-black p-0 font-Proxima"
                id="bulk"
                placeholder="Bulk Action"
                options={bulkOptions(selectedRows)}
                isClearable
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                value={selectedAction}
                isDisabled={selectedRows?.length === 0}
                onChange={handleAction}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: `1px solid #0000003B`,
                    borderColor: selectedCompany ? "#F15B29" : "#0000003B",
                    borderRadius: "0.3rem",
                    boxShadow: "none",
                    "&:hover": {
                      border: "1px solid #0000003B",
                    },
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? "#F15B29" : "white",
                    "&:active": {
                      backgroundColor: state.isSelected
                        ? "#F15B29"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  }),
                }}
              />
            )}
          </div>

          {/* Search Input */}
          <div className="w-full sm:flex-1 min-w-[180px]">
            <DebounceSearch
              className="w-full"
              search={searchValue}
              handleChange={setSearchValue}
              placeholder="Search..."
            />
          </div>

          {/* Export Button */}
          <div className="w-full sm:w-auto">
            <DefaultPrimaryButton
              value="EXPORT"
              onClick={handleSalesExportClick}
            />
          </div>
        </div>

        {loading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg mt-5 ">
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={getSalesListData}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={getSalesListData?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(getSalesListData?.total / pageLimit)}
            />
          </div>
        )}
      </div>
      {(isModal || confirmEmail) && (
        <div>
          <ViewSales
            onClose={() => {
              setIsModal(false);
              setConfirmEmail(false);
            }}
            selectedSaleId={selectedSaleId}
            setConfirmEmail={setConfirmEmail}
            confirmEmail={confirmEmail}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            type={type}
            onPriceUpdate={refreshSalesList}
          />
        </div>
      )}
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={
              actionType === "autoCount"
                ? "Pushed to AutoCount’s Quotation"
                : actionType === "unpaid"
                ? "Convert to Invoice"
                : actionType === "paid"
                ? "Convert to Receipt"
                : "Unknown Action"
            }
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => updateStatus(selectedSaleId, actionType)}
            isLoading={isLoading}
          />
        </div>
      )}
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={handelSaleDelete}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default SalesList;
